import type { Database } from "@/integrations/supabase/types";

export type QualificationPath =
  | "own_site"
  | "multi_site"
  | "client_portfolio"
  | "portfolio_pricing";
export type AccountUsagePath = "own_site" | "multi_site";

export type SiteOwnershipType = "own" | "client" | "mixed";
export type PricingNeed = "internal" | "agency_client" | "reseller_partner";
export type PortfolioPricingApplicationStatus =
  | "pending_review"
  | "under_review"
  | "approved"
  | "declined";
export type PortfolioPricingSubmissionSource = "signup" | "billing";
export type PortfolioPricingUiStatus = PortfolioPricingApplicationStatus | "none";

export type PortfolioPricingApplicationRow =
  Database["public"]["Tables"]["portfolio_pricing_applications"]["Row"];

export type PortfolioPricingApplicationInput = {
  businessName: string;
  companyUrl: string;
  roleAccountType: string;
  approximatePortfolioSize: string;
  currentSiteCount: string;
  expectedSiteCount: string;
  siteOwnershipType: SiteOwnershipType;
  useCaseSummary: string;
  primaryMarket: string;
  pricingNeed: PricingNeed;
  expectedOnboardingTimeframe: string;
};

export const QUALIFICATION_LABELS: Record<QualificationPath, string> = {
  own_site: "securing my own site",
  multi_site: "managing multiple WordPress sites",
  client_portfolio: "managing client or portfolio sites",
  portfolio_pricing: "applying for portfolio pricing",
};

export const DEFAULT_PORTFOLIO_PRICING_FORM: PortfolioPricingApplicationInput = {
  businessName: "",
  companyUrl: "",
  roleAccountType: "",
  approximatePortfolioSize: "",
  currentSiteCount: "",
  expectedSiteCount: "",
  siteOwnershipType: "mixed",
  useCaseSummary: "",
  primaryMarket: "",
  pricingNeed: "agency_client",
  expectedOnboardingTimeframe: "",
};

export const QUALIFICATION_OPTIONS: Array<{
  value: QualificationPath;
  label: string;
  description: string;
}> = [
  {
    value: "own_site",
    label: "I'm securing my own site",
    description: "Use the standard self-serve path for a single site or a small internal set.",
  },
  {
    value: "multi_site",
    label: "I manage multiple WordPress sites",
    description: "Continue on the standard path and add the first sites you want to secure.",
  },
];

export const ACCOUNT_USAGE_OPTIONS: Array<{
  value: AccountUsagePath;
  label: string;
  description: string;
}> = [
  {
    value: "own_site",
    label: "I'm securing my own site",
    description: "Use the standard self-serve path for your own site or a small internal set.",
  },
  {
    value: "multi_site",
    label: "I manage multiple WordPress sites",
    description: "Keep the standard path available and apply for reviewed portfolio pricing from Billing if needed.",
  },
];

export const PORTFOLIO_SIZE_OPTIONS = [
  "1-3 sites",
  "4-10 sites",
  "11-25 sites",
  "26-50 sites",
  "51-100 sites",
  "101-250 sites",
  "251+ sites",
] as const;

export const PORTFOLIO_PRICING_STATUS_LABELS: Record<
  PortfolioPricingApplicationStatus,
  string
> = {
  pending_review: "Pending review",
  under_review: "Under review",
  approved: "Approved",
  declined: "Declined",
};

export const getPortfolioPricingStatusTone = (
  status: PortfolioPricingApplicationStatus,
) => {
  if (status === "approved") return "success" as const;
  if (status === "declined") return "warning" as const;
  if (status === "under_review") return "info" as const;
  return "neutral" as const;
};

export const normalizeIntegerString = (value: string) =>
  value.replace(/[^\d]/g, "").replace(/^0+(?=\d)/, "");

export const isValidHttpUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

export const validatePortfolioPricingApplication = (
  form: PortfolioPricingApplicationInput,
) => {
  if (!form.businessName.trim()) {
    throw new Error("Business or agency name is required for a portfolio pricing application.");
  }

  if (!form.companyUrl.trim() || !isValidHttpUrl(form.companyUrl.trim())) {
    throw new Error("Enter a valid website URL for the business or agency applying.");
  }

  if (!form.roleAccountType.trim()) {
    throw new Error("Tell us your role or account type.");
  }

  if (!form.approximatePortfolioSize) {
    throw new Error("Choose the approximate portfolio size.");
  }

  if (!form.currentSiteCount.trim()) {
    throw new Error("Tell us how many websites you currently manage.");
  }

  if (!form.expectedSiteCount.trim()) {
    throw new Error("Tell us how many websites you expect to secure with ShadowScan.");
  }

  if (!form.useCaseSummary.trim()) {
    throw new Error("Add a short description of how you plan to use ShadowScan.");
  }

  if (!form.expectedOnboardingTimeframe) {
    throw new Error("Choose the expected onboarding timeframe.");
  }
};

export const normalizePortfolioPricingApplication = (
  form: PortfolioPricingApplicationInput,
): PortfolioPricingApplicationInput => ({
  ...form,
  businessName: form.businessName.trim(),
  companyUrl: form.companyUrl.trim(),
  roleAccountType: form.roleAccountType.trim(),
  currentSiteCount: form.currentSiteCount.trim(),
  expectedSiteCount: form.expectedSiteCount.trim(),
  useCaseSummary: form.useCaseSummary.trim(),
  primaryMarket: form.primaryMarket.trim(),
});

export const normalizeAccountUsagePath = (
  value: string | null | undefined,
): AccountUsagePath => {
  if (value === "multi_site" || value === "client_portfolio" || value === "portfolio_pricing") {
    return "multi_site";
  }

  return "own_site";
};
